import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, RefreshCw, Database, Cpu, Mail, ShieldAlert, ShieldCheck, FileText, Users, UserPlus, Link as LinkIcon } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_BADGE = {
  manual_added: 'bg-slate-100 text-slate-700',
  detected_by_qr: 'bg-indigo-100 text-indigo-800',
  detected_by_photo: 'bg-indigo-100 text-indigo-800',
  detected_by_wifi: 'bg-indigo-100 text-indigo-800',
  read_only_connected: 'bg-emerald-100 text-emerald-800',
  limited_control_available: 'bg-amber-100 text-amber-800',
  control_available: 'bg-amber-100 text-amber-800',
  control_enabled: 'bg-purple-100 text-purple-800',
  installer_required: 'bg-orange-100 text-orange-800',
  incompatible: 'bg-rose-100 text-rose-800',
  unsupported: 'bg-rose-100 text-rose-800',
  error: 'bg-rose-100 text-rose-800',
};

const SP_BADGE = {
  yes_safe: 'bg-emerald-100 text-emerald-800',
  conditional_check: 'bg-amber-100 text-amber-800',
  no_unsafe: 'bg-rose-100 text-rose-800',
  unknown: 'bg-slate-100 text-slate-700',
};

function StatBox({ label, value, hint, testId }) {
  return (
    <div className="rounded-xl border border-border bg-white p-4" data-testid={testId}>
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="text-2xl font-light tabular-nums mt-1">{value}</div>
      {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
    </div>
  );
}

export default function DeviceHubAdminTab({ token }) {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [overview, setOverview] = useState(null);
  const [devices, setDevices] = useState({ items: [] });
  const [learning, setLearning] = useState({ items: [] });
  const [requests, setRequests] = useState({ items: [] });
  const [kb, setKb] = useState({ items: [], patterns_total: 0, contacts_total: 0 });
  const [spRules, setSpRules] = useState({ rules: {} });
  const [draftBrand, setDraftBrand] = useState('daikin');
  const [draftModel, setDraftModel] = useState('altherma');
  const [draftCategory, setDraftCategory] = useState('heat_pump');
  const [draftPreview, setDraftPreview] = useState(null);
  // New-pilot form state
  const [newPilotEmail, setNewPilotEmail] = useState('');
  const [newPilotName, setNewPilotName] = useState('');
  const [newPilotType, setNewPilotType] = useState('residential');
  const [newPilotDays, setNewPilotDays] = useState(30);
  const [newPilotNotes, setNewPilotNotes] = useState('');
  const [newPilotLink, setNewPilotLink] = useState('');

  const headers = { Authorization: `Bearer ${token}` };

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const [o, d, l, r, k, sp] = await Promise.all([
        axios.get(`${API}/admin/device-hub/overview`, { headers }),
        axios.get(`${API}/admin/device-hub/devices?limit=100`, { headers }),
        axios.get(`${API}/admin/device-hub/learning-events?limit=50`, { headers }),
        axios.get(`${API}/admin/device-hub/data-requests?limit=50`, { headers }),
        axios.get(`${API}/admin/device-hub/knowledge-base?limit=100`, { headers }),
        axios.get(`${API}/admin/device-hub/smart-plug-rules`, { headers }),
      ]);
      setOverview(o.data);
      setDevices(d.data);
      setLearning(l.data);
      setRequests(r.data);
      setKb(k.data);
      setSpRules(sp.data);
    } catch (err) {
      toast.error(`Laden mislukt: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setLoading(false);
    }
  }, [token]); // eslint-disable-line

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const onSeedKb = async () => {
    if (!window.confirm('Knowledge base seed (idempotent — alleen nieuwe rijen worden ingevoegd)?')) return;
    try {
      setBusy(true);
      const r = await axios.post(`${API}/admin/device-hub/knowledge-base/seed`, {}, { headers });
      toast.success(`Seed klaar: ${r.data.inserted_models} models · ${r.data.inserted_patterns} patterns · ${r.data.inserted_contacts} contacts`);
      await fetchAll();
    } catch (err) {
      toast.error(`Seed mislukt: ${err?.response?.data?.detail || err.message}`);
    } finally { setBusy(false); }
  };

  const onCreateDraft = async () => {
    try {
      setBusy(true);
      const r = await axios.post(`${API}/admin/device-hub/data-requests`, {
        brand: draftBrand.trim().toLowerCase(),
        model: draftModel.trim().toLowerCase(),
        category: draftCategory,
        missing_fields: ['api_availability', 'control_actions', 'safety_limits', 'smart_plug_suitability'],
      }, { headers });
      setDraftPreview(r.data);
      toast.success('Draft aangemaakt (geen mail verstuurd)');
      await fetchAll();
    } catch (err) {
      toast.error(`Draft mislukt: ${err?.response?.data?.detail || err.message}`);
    } finally { setBusy(false); }
  };

  const onCreatePilot = async () => {
    if (!newPilotEmail.trim() || !newPilotName.trim()) {
      toast.error('Email en naam zijn verplicht');
      return;
    }
    try {
      setBusy(true);
      const payload = {
        email: newPilotEmail.trim().toLowerCase(),
        name: newPilotName.trim(),
        pilot_type: newPilotType,
        expires_in_days: parseInt(newPilotDays, 10) || 30,
        notes: newPilotNotes.trim(),
      };
      const r = await axios.post(`${API}/admin/pilot-access`, payload, { headers });
      const link = `${window.location.origin}/my-devices?email=${encodeURIComponent(payload.email)}`;
      setNewPilotLink(link);
      toast.success(`Pilot ${r.data.pilot.email} aangemaakt`);
      setNewPilotEmail(''); setNewPilotName(''); setNewPilotNotes('');
      await fetchAll();
    } catch (err) {
      toast.error(`Pilot aanmaken mislukt: ${err?.response?.data?.detail || err.message}`);
    } finally { setBusy(false); }
  };

  if (loading || !overview) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="device-hub-loading">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="device-hub-admin-tab">
      {/* HEADER */}
      <div className="rounded-2xl border border-emerald-300 bg-emerald-50 p-4 space-y-2" data-testid="device-hub-header">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-xl font-light text-foreground">Device Hub — P0a Pilot Core</h2>
            <span className="text-[10px] uppercase tracking-wider rounded-full bg-slate-900 text-white px-2 py-0.5">P0a</span>
            <span className="text-[10px] uppercase tracking-wider rounded-full bg-emerald-200 text-emerald-900 px-2 py-0.5" data-testid="dh-mode-badge">
              read-only / limited-control pilot
            </span>
            <span className="text-[10px] uppercase tracking-wider rounded-full bg-rose-100 text-rose-800 px-2 py-0.5">
              no auto-steering
            </span>
            <span className="text-[10px] uppercase tracking-wider rounded-full bg-rose-100 text-rose-800 px-2 py-0.5">
              no vendor mail-send
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={fetchAll} disabled={busy} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-border bg-white text-xs hover:bg-slate-50 disabled:opacity-50" data-testid="dh-refresh-btn">
              <RefreshCw className={`w-3 h-3 ${busy ? 'animate-spin' : ''}`} /> Vernieuw
            </button>
            <button onClick={onSeedKb} disabled={busy} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-primary text-white text-xs hover:opacity-90 disabled:opacity-50" data-testid="dh-kb-seed-btn">
              <Database className="w-3 h-3" /> KB seed
            </button>
          </div>
        </div>
        <div className="text-xs text-emerald-900 leading-relaxed">
          <strong>Veiligheid:</strong> meten + herkennen + advies. Geen automatische sturing. Geen fabrikantmail wordt verstuurd. Smart-plug-advies alleen voor categorieën met <code>yes_safe</code>.
        </div>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="dh-kpis">
        <StatBox label="Total devices" value={overview.total_devices} testId="dh-kpi-devices" />
        <StatBox label="Active pilots" value={overview.pilots.length} testId="dh-kpi-pilots" />
        <StatBox label="Learning events" value={overview.learning_events_count} testId="dh-kpi-learning" />
        <StatBox label="Data req drafts" value={overview.data_request_drafts_count} hint={`${overview.data_requests_sent_count} sent (always 0 in P0a)`} testId="dh-kpi-drafts" />
        <StatBox label="KB models" value={kb.count} hint={`${kb.patterns_total} patterns · ${kb.contacts_total} contacts`} testId="dh-kpi-kb" />
      </div>

      {/* NEW PILOT FORM */}
      <div className="rounded-2xl border border-border bg-white p-4 space-y-3" data-testid="dh-new-pilot">
        <h3 className="text-sm font-medium flex items-center gap-2"><UserPlus className="w-4 h-4" /> Nieuwe pilot aanmaken</h3>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-2 text-xs">
          <input value={newPilotEmail} onChange={(e) => setNewPilotEmail(e.target.value)} placeholder="email" className="border border-border rounded px-2 py-1.5" data-testid="dh-new-pilot-email" />
          <input value={newPilotName} onChange={(e) => setNewPilotName(e.target.value)} placeholder="naam / label" className="border border-border rounded px-2 py-1.5" data-testid="dh-new-pilot-name" />
          <select value={newPilotType} onChange={(e) => setNewPilotType(e.target.value)} className="border border-border rounded px-2 py-1.5" data-testid="dh-new-pilot-type">
            <option value="residential">residential</option>
            <option value="commercial">commercial</option>
            <option value="mkb">mkb</option>
          </select>
          <input type="number" min="1" max="365" value={newPilotDays} onChange={(e) => setNewPilotDays(e.target.value)} placeholder="expires_in_days" className="border border-border rounded px-2 py-1.5" data-testid="dh-new-pilot-days" />
          <button onClick={onCreatePilot} disabled={busy} className="inline-flex items-center justify-center gap-1 px-3 py-1.5 rounded-lg bg-primary text-white hover:opacity-90 disabled:opacity-50" data-testid="dh-new-pilot-submit">
            <UserPlus className="w-3 h-3" /> Aanmaken
          </button>
        </div>
        <textarea value={newPilotNotes} onChange={(e) => setNewPilotNotes(e.target.value)} placeholder="notes (optioneel)" rows={2} className="w-full text-xs border border-border rounded px-2 py-1.5" data-testid="dh-new-pilot-notes" />
        <div className="text-[11px] text-muted-foreground">
          Geen mail wordt verstuurd. Pilot kan direct gebruiken via de link hieronder.
        </div>
        {newPilotLink && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-2 text-[11px] flex items-center gap-2" data-testid="dh-new-pilot-link">
            <LinkIcon className="w-3 h-3 text-emerald-700 shrink-0" />
            <a href={newPilotLink} target="_blank" rel="noreferrer" className="font-mono text-emerald-800 underline break-all">{newPilotLink}</a>
            <button onClick={() => { navigator.clipboard.writeText(newPilotLink); toast.success('Link gekopieerd'); }} className="ml-auto text-emerald-700 underline shrink-0">copy</button>
          </div>
        )}
      </div>

      {/* PILOTS */}
      <div className="rounded-2xl border border-border bg-white p-4" data-testid="dh-pilots">
        <h3 className="text-sm font-medium mb-3 flex items-center gap-2"><Users className="w-4 h-4" /> Pilots</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-muted-foreground border-b border-border">
              <tr className="text-left">
                <th className="py-1.5 pr-2">Pilot email</th>
                <th className="py-1.5 pr-2 text-right">Devices</th>
                <th className="py-1.5 pr-2 text-right">Read-only conn.</th>
                <th className="py-1.5 pr-2 text-right">Manual</th>
                <th className="py-1.5 pr-2">Categories</th>
                <th className="py-1.5 pr-2">Last added</th>
              </tr>
            </thead>
            <tbody>
              {overview.pilots.map((p) => (
                <tr key={p.pilot_email} className="border-b border-border last:border-b-0">
                  <td className="py-1 pr-2 font-mono text-[10px]">{p.pilot_email}</td>
                  <td className="py-1 pr-2 text-right tabular-nums">{p.devices_count}</td>
                  <td className="py-1 pr-2 text-right tabular-nums">{p.read_only_count}</td>
                  <td className="py-1 pr-2 text-right tabular-nums">{p.manual_count}</td>
                  <td className="py-1 pr-2 text-[10px]">{p.categories.join(', ')}</td>
                  <td className="py-1 pr-2 text-[10px]">{p.last_added_at}</td>
                </tr>
              ))}
              {overview.pilots.length === 0 && (
                <tr><td colSpan={6} className="py-3 text-center text-muted-foreground">Nog geen pilots met devices. Maak een pilot-user aan via de bestaande pilot-access flow en voeg via API een device toe.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* DEVICES */}
      <div className="rounded-2xl border border-border bg-white p-4" data-testid="dh-devices">
        <h3 className="text-sm font-medium mb-3 flex items-center gap-2"><Cpu className="w-4 h-4" /> Devices (laatste 100)</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-muted-foreground border-b border-border">
              <tr className="text-left">
                <th className="py-1.5 pr-2">Pilot</th>
                <th className="py-1.5 pr-2">Apparaat</th>
                <th className="py-1.5 pr-2">Categorie</th>
                <th className="py-1.5 pr-2">Status</th>
                <th className="py-1.5 pr-2">Confidence</th>
                <th className="py-1.5 pr-2">Last reading</th>
              </tr>
            </thead>
            <tbody>
              {devices.items.map((d) => (
                <tr key={d.id} className="border-b border-border last:border-b-0">
                  <td className="py-1 pr-2 font-mono text-[10px]">{d.pilot_email}</td>
                  <td className="py-1 pr-2">{d.display_name} <span className="text-muted-foreground text-[10px]">({d.brand} {d.model})</span></td>
                  <td className="py-1 pr-2 font-mono text-[10px]">{d.category}</td>
                  <td className="py-1 pr-2">
                    <span className={`text-[10px] uppercase rounded-full px-2 py-0.5 ${STATUS_BADGE[d.status] || ''}`}>{d.status}</span>
                  </td>
                  <td className="py-1 pr-2 tabular-nums">{(d.confidence_score * 100).toFixed(0)}%</td>
                  <td className="py-1 pr-2 text-[10px]">{d.last_reading_at || '—'}</td>
                </tr>
              ))}
              {devices.items.length === 0 && (
                <tr><td colSpan={6} className="py-3 text-center text-muted-foreground">Nog geen devices toegevoegd.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* LEARNING EVENTS + DATA REQUESTS */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-border bg-white p-4" data-testid="dh-learning">
          <h3 className="text-sm font-medium mb-3 flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> Learning events (audit-only)</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-muted-foreground border-b border-border">
                <tr className="text-left">
                  <th className="py-1.5 pr-2">Type</th>
                  <th className="py-1.5 pr-2">Brand/Model</th>
                  <th className="py-1.5 pr-2">Method</th>
                  <th className="py-1.5 pr-2">Resulted in</th>
                </tr>
              </thead>
              <tbody>
                {learning.items.slice(0, 12).map((e) => (
                  <tr key={e.id} className="border-b border-border last:border-b-0">
                    <td className="py-1 pr-2 font-mono text-[10px]">{e.event_type}</td>
                    <td className="py-1 pr-2 text-[11px]">{(e.payload || {}).brand} {(e.payload || {}).model}</td>
                    <td className="py-1 pr-2 text-[10px]">{(e.payload || {}).match_method || '—'}</td>
                    <td className="py-1 pr-2 text-[10px]">{e.resulted_in}</td>
                  </tr>
                ))}
                {learning.items.length === 0 && (
                  <tr><td colSpan={4} className="py-3 text-center text-muted-foreground">Geen events.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="rounded-2xl border border-border bg-white p-4 space-y-3" data-testid="dh-data-requests">
          <h3 className="text-sm font-medium flex items-center gap-2"><Mail className="w-4 h-4" /> Component Data Requests (DRAFT only)</h3>
          <div className="space-y-2">
            <div className="grid grid-cols-3 gap-2">
              <input value={draftBrand} onChange={(e) => setDraftBrand(e.target.value)} placeholder="brand" className="text-xs border border-border rounded px-2 py-1" data-testid="dh-draft-brand" />
              <input value={draftModel} onChange={(e) => setDraftModel(e.target.value)} placeholder="model" className="text-xs border border-border rounded px-2 py-1" data-testid="dh-draft-model" />
              <select value={draftCategory} onChange={(e) => setDraftCategory(e.target.value)} className="text-xs border border-border rounded px-2 py-1" data-testid="dh-draft-category">
                {Object.keys(spRules.rules || {}).map((cat) => <option key={cat} value={cat}>{cat}</option>)}
              </select>
            </div>
            <button onClick={onCreateDraft} disabled={busy} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-slate-900 text-white text-xs hover:opacity-90 disabled:opacity-50" data-testid="dh-create-draft-btn">
              <FileText className="w-3 h-3" /> Genereer draft (geen mail verzenden)
            </button>
          </div>
          {draftPreview && (
            <div className="bg-slate-50 border border-border rounded-lg p-2 text-[10px] font-mono whitespace-pre-wrap max-h-48 overflow-y-auto" data-testid="dh-draft-preview">
              <div className="text-emerald-700 mb-1">recipient: {draftPreview.recipient_email || '(none — admin moet adres invullen)'} · status: {draftPreview.status} · dry_run: {String(draftPreview.dry_run)}</div>
              {draftPreview.draft_text}
            </div>
          )}
          <div className="border-t border-border pt-2 text-xs">
            <div className="font-medium mb-1">Recent drafts ({requests.count}):</div>
            <ul className="space-y-1 max-h-48 overflow-y-auto">
              {requests.items.slice(0, 10).map((r) => (
                <li key={r.id} className="text-[11px] text-muted-foreground">
                  {r.brand} {r.model} <span className="font-mono">[{r.category}]</span> · {r.status} · {r.created_at}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* SMART PLUG SAFETY MATRIX */}
      <div className="rounded-2xl border border-border bg-white p-4" data-testid="dh-smart-plug-matrix">
        <h3 className="text-sm font-medium mb-3 flex items-center gap-2"><ShieldAlert className="w-4 h-4" /> Smart-plug safety matrix</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
          {Object.entries(spRules.rules || {}).map(([cat, rule]) => (
            <div key={cat} className="bg-slate-50 rounded-lg p-2 border border-border">
              <div className="flex items-center justify-between gap-1">
                <span className="font-mono text-[11px]">{cat}</span>
                <span className={`text-[9px] uppercase rounded-full px-1.5 py-0.5 ${SP_BADGE[rule.suitable] || ''}`}>{rule.suitable}</span>
              </div>
              <div className="text-[10px] text-muted-foreground mt-1">{rule.reason}</div>
            </div>
          ))}
        </div>
      </div>

      {/* KNOWLEDGE BASE */}
      <div className="rounded-2xl border border-border bg-white p-4" data-testid="dh-knowledge-base">
        <h3 className="text-sm font-medium mb-3 flex items-center gap-2"><Database className="w-4 h-4" /> Knowledge base — {kb.count} models</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-muted-foreground border-b border-border">
              <tr className="text-left">
                <th className="py-1.5 pr-2">Brand</th>
                <th className="py-1.5 pr-2">Model</th>
                <th className="py-1.5 pr-2">Category</th>
                <th className="py-1.5 pr-2">Smart-plug</th>
                <th className="py-1.5 pr-2">Status</th>
                <th className="py-1.5 pr-2 text-right">Confidence</th>
              </tr>
            </thead>
            <tbody>
              {kb.items.slice(0, 40).map((m) => (
                <tr key={m.id} className="border-b border-border last:border-b-0">
                  <td className="py-1 pr-2 font-mono text-[10px]">{m.brand}</td>
                  <td className="py-1 pr-2 font-mono text-[10px]">{m.model}</td>
                  <td className="py-1 pr-2 text-[10px]">{m.category}</td>
                  <td className="py-1 pr-2">
                    <span className={`text-[9px] uppercase rounded-full px-1.5 py-0.5 ${SP_BADGE[m.smart_plug_suitable] || ''}`}>{m.smart_plug_suitable}</span>
                  </td>
                  <td className="py-1 pr-2 text-[10px]">{m.status}</td>
                  <td className="py-1 pr-2 text-right tabular-nums">{(m.confidence * 100).toFixed(0)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
